/**
 * Chat-based data logging API client.
 *
 * Provides functions for:
 * - Sending natural language messages to log water test data
 * - Undoing/deleting logged entries
 */

import { buildApiUrl } from './config';
import { apiClient } from './client';

export interface LoggedEntry {
  id: number;
  parameter: string;
  value: number;
  unit: string;
  recorded_at: string;
  tank_name: string;
}

export interface ChatLogResponse {
  status: 'logged' | 'error';
  entry?: LoggedEntry;
  message?: string;
  examples?: string[];
  show_manual_form?: boolean;
  can_undo?: boolean;
}

export interface DeleteResponse {
  status: 'deleted';
  message: string;
}

/**
 * Send a chat message to log water test data.
 *
 * @param tankId - The tank ID to log the measurement for
 * @param message - Natural language message (e.g., "nitrates 5", "alk 8.2 yesterday")
 * @returns ChatLogResponse with logged entry or error details
 */
export async function sendChatLog(
  tankId: string,
  message: string
): Promise<ChatLogResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/chat/log`);

  const response = await apiClient(url, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Undo/delete a logged entry.
 *
 * @param entryId - The measurement entry ID to delete
 * @returns DeleteResponse confirming deletion
 */
export async function undoChatLog(entryId: number): Promise<DeleteResponse> {
  const url = buildApiUrl(`/api/chat/log/${entryId}`);

  const response = await apiClient(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

// Dosing chat types and functions

export interface DosingLoggedEntry {
  id: number;
  chemical_name: string;
  volume_ml: number;
  recorded_at: string;
  brand?: string;
  tank_name: string;
}

export interface DosingChatResponse {
  status: 'logged' | 'error';
  entry?: DosingLoggedEntry;
  message?: string;
  examples?: string[];
  show_manual_form?: boolean;
  can_undo?: boolean;
}

/**
 * Send a chat message to log dosing data.
 *
 * @param tankId - The tank ID to log the dosing for
 * @param message - Natural language message (e.g., "mag 60ml", "dosed 50ml alk yesterday")
 * @returns DosingChatResponse with logged entry or error details
 */
export async function sendDosingChatLog(
  tankId: string,
  message: string
): Promise<DosingChatResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/chat/dosing`);

  const response = await apiClient(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Undo/delete a logged dosing entry.
 *
 * @param entryId - The dosing entry ID to delete
 * @returns Response confirming deletion
 */
export async function undoDosingChatLog(
  entryId: number
): Promise<{ status: string; message: string }> {
  const url = buildApiUrl(`/api/chat/dosing/${entryId}`);

  const response = await apiClient(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to undo dosing entry');
  }

  return response.json();
}

// Livestock chat types

export interface SpeciesCandidate {
  id: number;
  common_name: string;
  scientific_name: string | null;
  care_level: string | null;
  max_size_inches: number | null;
}

export interface LivestockMatch {
  id: number;
  common_name: string;
  scientific_name: string | null;
  acquisition_date: string;
  status: string;
}

export interface LivestockParsedData {
  species_query: string | null;
  quantity: number;
  vendor: string | null;
  price: number | null;
  currency: string;
  date: string | null;
}

export interface LivestockAddedEntry {
  id: number;
  common_name: string;
  scientific_name: string | null;
  quantity: number;
  acquisition_date: string;
  tank_name: string;
}

export interface ConfirmLivestockAddRequest {
  species_id?: number | null;
  common_name: string;
  scientific_name?: string | null;
  quantity: number;
  acquisition_date: string;
  vendor?: string | null;
  price_paid?: number | null;
  currency?: string;
}

export interface ConfirmLivestockDeceasedRequest {
  livestock_id: number;
  deceased_date?: string | null;
  notes?: string | null;
}

// Task matching types

export interface MatchingTestTask {
  instance_id: string;
  definition_id: string;
  task_name: string;
  due_at: string;
  is_overdue: boolean;
  definition_status: 'active' | 'paused';
  has_recurrence: boolean;
  next_due_preview?: string | null;
}

// Unified chat types and functions

export interface UnifiedChatResponse {
  status: 'logged' | 'error' | 'needs_selection';
  entry_type?: 'water_test' | 'dosing' | 'livestock_add' | 'livestock_deceased';
  water_test_entry?: LoggedEntry;
  dosing_entry?: DosingLoggedEntry;
  // Livestock fields
  livestock_entry?: LivestockAddedEntry;
  species_candidates?: SpeciesCandidate[];
  livestock_matches?: LivestockMatch[];
  livestock_parsed?: LivestockParsedData;
  // Task matching
  matching_task?: MatchingTestTask | null;
  // Common fields
  message?: string;
  examples?: string[];
  show_manual_form?: boolean;
  can_undo?: boolean;
}

/**
 * Send a chat message with auto-detection of entry type.
 *
 * Automatically detects whether the message is a water test measurement
 * or a dosing entry based on the content.
 *
 * @param tankId - The tank ID to log the entry for
 * @param message - Natural language message (e.g., "alk 8.2", "mag 60ml")
 * @returns UnifiedChatResponse with logged entry or error details
 */
export async function sendUnifiedChatLog(
  tankId: string,
  message: string
): Promise<UnifiedChatResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/chat/unified`);

  const response = await apiClient(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Confirm livestock add after species selection.
 *
 * @param tankId - The tank ID to add livestock to
 * @param data - The livestock add request data
 * @returns UnifiedChatResponse with logged entry or error details
 */
export async function confirmLivestockAdd(
  tankId: string,
  data: ConfirmLivestockAddRequest
): Promise<UnifiedChatResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/chat/livestock/confirm`);

  const response = await apiClient(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Confirm livestock deceased after selection.
 *
 * @param tankId - The tank ID
 * @param data - The livestock deceased request data
 * @returns UnifiedChatResponse with update confirmation or error details
 */
export async function confirmLivestockDeceased(
  tankId: string,
  data: ConfirmLivestockDeceasedRequest
): Promise<UnifiedChatResponse> {
  const url = buildApiUrl(`/api/tanks/${tankId}/chat/livestock/deceased`);

  const response = await apiClient(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}
